import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLoader from '../components/PageLoader';
import ResearchRegionsWorkspace, { getCachedResearchRegionsPayload, loadResearchRegionsPayload } from '../components/ResearchRegionsWorkspace';
import { apiJson, isUnauthorizedError } from '../services/api';
import './Research.css';

export { CompactMarketIndex, filterMarketsByState } from '../components/ResearchRegionsWorkspace';

const loadEvictionMap = () => import('../components/LscEvictionTrackerMapSection');
const loadStateMap = () => import('../components/StateGrossYieldScreeningSection');
const LscEvictionTrackerMapSection = lazy(loadEvictionMap);
const StateGrossYieldScreeningSection = lazy(loadStateMap);

const pageCache = {
  lsc: null,
  states: null,
};

const pendingRequests = {};
function cachedRequest(key, url) {
  if (pageCache[key]) return Promise.resolve(pageCache[key]);
  if (!pendingRequests[key]) {
    pendingRequests[key] = apiJson(url)
      .then((payload) => {
        pageCache[key] = payload || null;
        return pageCache[key];
      })
      .finally(() => {
        delete pendingRequests[key];
      });
  }
  return pendingRequests[key];
}

function LazyMapFallback({ activeTab, onSelect, label }) {
  return (
    <section className="research-lazy-map-fallback">
      <ScreeningTabs activeTab={activeTab} onSelect={onSelect} />
      <PageLoader compact label={label} />
    </section>
  );
}

export function ScreeningTabs({ activeTab, onSelect }) {
  return (
    <div className="research-screening-tabs" role="tablist" aria-label="U.S. screening views">
      <button type="button" role="tab" aria-selected={activeTab === 'regions'} className={activeTab === 'regions' ? 'is-active' : ''} onClick={() => onSelect('regions')}>1% Regions + Hazard Risk</button>
      <button type="button" role="tab" aria-selected={activeTab === 'states'} className={activeTab === 'states' ? 'is-active' : ''} onClick={() => onSelect('states')}>State 1% Rule</button>
      <button type="button" role="tab" aria-selected={activeTab === 'evictions'} className={activeTab === 'evictions' ? 'is-active' : ''} onClick={() => onSelect('evictions')}>Eviction filings</button>
    </div>
  );
}

export default function Research() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('regions');
  const [stateTabOpened, setStateTabOpened] = useState(Boolean(pageCache.states));
  const [evictionTabOpened, setEvictionTabOpened] = useState(false);
  const [femaPayload, setFemaPayload] = useState(getCachedResearchRegionsPayload());
  const [femaLoading, setFemaLoading] = useState(!getCachedResearchRegionsPayload());
  const [femaError, setFemaError] = useState('');
  const [lscPayload, setLscPayload] = useState(pageCache.lsc);
  const [lscLoading, setLscLoading] = useState(false);
  const [lscError, setLscError] = useState('');
  const [statePayload, setStatePayload] = useState(pageCache.states);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateError, setStateError] = useState('');

  useEffect(() => {
    let active = true;
    const handleError = (error, setter, fallback) => {
      if (!active) return;
      if (isUnauthorizedError(error)) {
        navigate('/login', { replace: true });
        return;
      }
      setter(error?.message || fallback);
    };

    if (!getCachedResearchRegionsPayload()) {
      loadResearchRegionsPayload(apiJson)
        .then((payload) => active && setFemaPayload(payload))
        .catch((error) => handleError(error, setFemaError, 'Could not load the FEMA screening map.'))
        .finally(() => active && setFemaLoading(false));
    }
    return () => { active = false; };
  }, [navigate]);

  useEffect(() => {
    if (activeTab !== 'evictions' || lscPayload) return undefined;
    let active = true;
    setLscLoading(true);
    setLscError('');
    cachedRequest('lsc', '/research/lsc-eviction-map/us')
      .then((payload) => active && setLscPayload(payload))
      .catch((error) => {
        if (!active) return;
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        setLscError(error?.message || 'Could not load the LSC eviction map.');
      })
      .finally(() => active && setLscLoading(false));
    return () => { active = false; };
  }, [activeTab, lscPayload, navigate]);

  useEffect(() => {
    if (statePayload) return undefined;
    let active = true;
    const load = () => {
      if (!active) return;
      setStateLoading(true);
      setStateError('');
      cachedRequest('states', '/research/state-gross-yield/us')
        .then((payload) => active && setStatePayload(payload))
        .catch((error) => {
          if (!active) return;
          if (isUnauthorizedError(error)) {
            navigate('/login', { replace: true });
            return;
          }
          setStateError(error?.message || 'Could not load state 1%-rule data.');
        })
        .finally(() => active && setStateLoading(false));
    };
    if (activeTab === 'states') load();
    return () => { active = false; };
  }, [activeTab, navigate, statePayload]);

  function selectTab(nextTab) {
    if (nextTab === 'states') setStateTabOpened(true);
    if (nextTab === 'evictions') setEvictionTabOpened(true);
    setActiveTab(nextTab);
  }

  return (
    <div className="research-page">
      <div className="research-shell">
        <section className="research-native-section">
          <div className="research-screening-tabs-demo">
            <div role="tabpanel" hidden={activeTab !== 'regions'}>
              <ResearchRegionsWorkspace active={activeTab === 'regions'} payload={femaPayload} loading={femaLoading} error={femaError} viewTabs={<ScreeningTabs activeTab={activeTab} onSelect={selectTab} />} showSourceAudit />
            </div>
            {stateTabOpened ? (
              <div role="tabpanel" hidden={activeTab !== 'states'}>
                <Suspense fallback={<LazyMapFallback activeTab={activeTab} onSelect={selectTab} label="Loading state map" />}>
                  <div className="research-state-yield-demo"><StateGrossYieldScreeningSection active={activeTab === 'states'} payload={statePayload} loading={stateLoading} error={stateError} viewTabs={<ScreeningTabs activeTab={activeTab} onSelect={selectTab} />} /></div>
                </Suspense>
              </div>
            ) : null}
            {evictionTabOpened ? (
              <div role="tabpanel" hidden={activeTab !== 'evictions'}>
                <Suspense fallback={<LazyMapFallback activeTab={activeTab} onSelect={selectTab} label="Loading eviction map" />}>
                  <div className="research-tabbed-lsc">
                    <LscEvictionTrackerMapSection
                      loading={lscLoading}
                      error={lscError}
                      payload={lscPayload}
                      title="LSC Eviction Tracker | U.S. County Heat Map"
                      // subtitle="What is it? Eviction filing data. Where from? LSC county eviction filing data."
                      viewTabs={<ScreeningTabs activeTab={activeTab} onSelect={selectTab} />}
                      compactSplit
                      showSourceAudit
                    />
                  </div>
                </Suspense>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
